import React, { useState, useEffect } from 'react';
import { Plus, X, Check, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AiChatbotButtonInputs = ({
  value = [],
  onChange,
  onSave,
  placeholder = "Enter button text...",
  label = "Specific Button Texts",
  helpText = "Add button texts that should trigger AI replies. If empty, AI replies to all buttons.",
}) => {
  // Ensure value is always an array
  const safeValue = Array.isArray(value) ? value : (value ? [value] : []);
  const [buttons, setButtons] = useState(safeValue);
  const [newButtonText, setNewButtonText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Update buttons when value prop changes
  useEffect(() => {
    const newSafeValue = Array.isArray(value) ? value : (value ? [value] : []);
    setButtons(newSafeValue);
  }, [value]);

  const handleAddButton = () => {
    const trimmedText = newButtonText.trim();
    if (trimmedText && !buttons.includes(trimmedText)) {
      const updatedButtons = [...buttons, trimmedText];
      setButtons(updatedButtons);
      setNewButtonText('');
      handleSave(updatedButtons);
    }
  };

  const handleRemoveButton = (index) => {
    const updatedButtons = buttons.filter((_, i) => i !== index);
    setButtons(updatedButtons);
    handleSave(updatedButtons);
  };

  const handleSave = async (buttonsToSave = buttons) => {
    setIsSaving(true);
    try {
      onChange(buttonsToSave);
      if (onSave) {
        await onSave(buttonsToSave);
      }
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && newButtonText.trim()) {
      e.preventDefault();
      handleAddButton();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-1">
            {label}
          </label>
          {helpText && (
            <p className="text-xs text-gray-600">{helpText}</p>
          )}
        </div>
        {justSaved && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="flex items-center gap-1.5 text-xs text-[#01CC54] font-medium"
          >
            <Check size={14} />
            Saved
          </motion.div>
        )}
      </div>

      {/* Existing Buttons */}
      <div className="space-y-2">
        <AnimatePresence>
          {buttons.map((button, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-lg"
            >
              <span className="flex-1 text-sm text-gray-900 px-2 py-1">
                {button}
              </span>
              <button
                onClick={() => handleRemoveButton(index)}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                type="button"
              >
                <X size={16} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Add New Button Input */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newButtonText}
          onChange={(e) => setNewButtonText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#01CC54]/40 focus:border-[#01CC54] transition"
        />
        <button
          onClick={handleAddButton}
          disabled={!newButtonText.trim() || buttons.includes(newButtonText.trim())}
          className={`p-2 rounded-lg transition-all ${
            !newButtonText.trim() || buttons.includes(newButtonText.trim())
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-[#01CC54] text-white hover:bg-[#01CC54]/90'
          }`}
          type="button"
        >
          <Plus size={18} />
        </button>
      </div>

      {buttons.length === 0 && (
        <p className="text-xs text-gray-500 italic">
          No buttons added. AI will reply to all button clicks if this is empty.
        </p>
      )}
    </div>
  );
};

export default AiChatbotButtonInputs;

